import { alpha, Box, Card, Grid, Stack, Tab, Tabs, Typography, useTheme } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { SyntheticEvent, useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router';

import { enqueueSnackbar, formatAmount, Label, Spinner, useTable, useUserDetailsStore } from '@zeta-core';
import { BarGraphIcon, GraphIcon, TrendDownIcon, TrendUpIcon } from '@zeta-core/assets';
import { FinancialMisService, getErrorMessage } from '@zeta-services';
import { IClientCardItem, IEmptyRequestResponse, IFactFindEntity, IGenericErrorResponse } from '@zeta-types';
import AccountTable from './account-table';
import { PerformanceGraphs } from './performance-graph';
import PortfolioChart from './portfolio-pie-chart';

export const Portfolio = ({ isClient }: { isClient?: boolean }) => {
  const userStore = useUserDetailsStore((state) => state.user);

  const location = useLocation();
  const state = (location.state || {}) as { entityData: IFactFindEntity; clientData: IClientCardItem };
  const clientID = isClient ? userStore?.accessibleEntityID : state?.clientData?.clientID;

  const [currentTab, setCurrentTab] = useState('all');
  const [tableData, setTableData] = useState([]);
  const [portfolioAllocation, setPortfolioAllocation] = useState([]);
  const table = useTable({ defaultRowsPerPage: 5 });
  const theme = useTheme();

  const [tabs, setTabs] = useState([
    {
      value: 'all',
      label: 'All',
      count: 0,
    },
    {
      value: 'sole',
      label: 'Sole Account',
      count: 0,
    },
    {
      value: 'joint',
      label: 'Joint Account',
      count: 0,
    },
  ]);

  const handleChangeTab = useCallback((_event: SyntheticEvent, newValue: string) => {
    setCurrentTab(newValue);
  }, []);

  const portfolioAccounts = useMutation({
    mutationFn: (payload: IEmptyRequestResponse) =>
      isClient ? FinancialMisService().getPortfolioAccounts(payload) : FinancialMisService().getPortfolioAccountsAdvisor(payload),
    onSuccess: (response) => {
      setTableData(response?.data);
      const soleCount = response?.data?.accounts?.filter((item) => {
        return item?.accountType === 'Sole';
      }).length;
      const jointCount = response?.data?.accounts?.filter((item) => {
        return item?.accountType === 'Joint';
      }).length;
      const shouldShowTabs = Array.isArray(response?.data?.accounts) && response?.data?.accounts?.some((item) => item.accountType != null);
      const newTabs = tabs?.map((item) => {
        if (item.value === 'sole') {
          return {
            ...item,
            count: soleCount,
          };
        }
        if (item.value === 'joint') {
          return {
            ...item,
            count: jointCount,
          };
        }
        if (shouldShowTabs) {
          return {
            ...item,
            count: response?.data?.accounts.length,
          };
        }
        return {
          ...item,
        };
      });
      setTabs(newTabs);
    },
    onError: (error: IGenericErrorResponse) => {
      const errorCode = error?.response?.data?.error?.errorCode || error?.response?.data?.error[0]?.errorCode;
      enqueueSnackbar(getErrorMessage(errorCode), { variant: 'error' });
    },
  });

  useEffect(() => {
    if (clientID) {
      const payload = { clientID, pageSize: table?.rowsPerPage, pageIndex: table?.page + 1 };
      portfolioAccounts?.mutateAsync(payload);
    }
  }, [table?.rowsPerPage, table?.page, clientID]);

  const getPortfolioAllocation = useMutation({
    mutationFn: (payload: IEmptyRequestResponse) =>
      isClient ? FinancialMisService().getPortfolioAllocation(payload) : FinancialMisService().getPortfolioAllocationAdvisor(payload),
    onSuccess: (response) => {
      setPortfolioAllocation(response?.data);
    },
    onError: (error: IGenericErrorResponse) => {
      const errorCode = error?.response?.data?.error?.errorCode || error?.response?.data?.error[0]?.errorCode;
      enqueueSnackbar(getErrorMessage(errorCode), { variant: 'error' });
    },
  });

  useEffect(() => {
    if (clientID) {
      const payload = { clientID };
      getPortfolioAllocation?.mutateAsync(payload);
    }
  }, [clientID]);

  const shouldShowTabs = Array.isArray(tableData?.accounts) && tableData?.accounts?.some((item) => item.accountType != null);

  const isEmpty = Object.keys(portfolioAllocation).length === 0;

  return (
    <Box
      sx={{
        backgroundSize: '18px 18px',
        p: 3,
      }}
    >
      <Typography variant="h3">Portfolio</Typography>
      <Typography variant="subtitle2" color="text.secondary">
        Get real-time insights into your investments and track your financial growth with ease.
      </Typography>
      <Grid container spacing={2} mt={2}>
        <Grid
          size={{
            lg: 4,
            md: 12,
            sm: 12
          }}>
          <Stack gap={2}>
            {portfolioAccounts?.isPending ? (
              <Stack minHeight="20vh" justifyContent="center" alignItems="center">
                <Spinner />
              </Stack>
            ) : (
              <Stack gap={2}>
                <Card sx={{ p: 2, backgroundColor: theme?.palette?.common?.white, border: `1px solid ${theme?.palette?.primary?.main}` }}>
                  <Stack direction="row" justifyContent="flex-start" alignItems="center">
                    <Box
                      width="50px"
                      height="50px"
                      sx={{
                        mr: 2,
                        color: theme?.palette?.common?.white,
                        backgroundColor: theme?.palette?.primary?.main,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: `5px solid ${alpha(theme?.palette?.primary?.lighter, 0.78)}`,
                      }}
                    >
                      <GraphIcon />
                    </Box>
                    <Stack>
                      <Typography variant="subtitle1" color="text.secondary">
                        Your Net Worth
                      </Typography>
                      <Typography variant="h4">{formatAmount(tableData?.netWorth)}</Typography>
                    </Stack>
                  </Stack>
                </Card>
                <Card sx={{ p: 2, backgroundColor: theme?.palette?.common?.white, border: `1px solid ${theme?.palette?.info?.main}` }}>
                  <Stack direction="row" justifyContent="flex-start" alignItems="center">
                    <Box
                      width="50px"
                      height="50px"
                      sx={{
                        mr: 2,
                        color: theme?.palette?.common?.white,
                        backgroundColor: theme?.palette?.primary?.main,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: `5px solid ${alpha(theme?.palette?.primary?.lighter, 0.78)}`,
                      }}
                    >
                      <BarGraphIcon />
                    </Box>
                    <Stack>
                      <Typography variant="subtitle1" color="text.secondary">
                        Total Portfolio Value
                      </Typography>
                      <Stack direction="row" alignItems="center">
                        <Typography variant="h4" mr={1}>
                          {formatAmount(tableData?.totalPortfolioValue)}{' '}
                        </Typography>
                        {tableData?.dailyPortfolioChangePct ? (
                          <>
                            {tableData?.dailyPortfolioChangePct > 0 ? <TrendUpIcon /> : <TrendDownIcon />}
                            <Typography
                              variant="subtitle2"
                              sx={{ color: tableData?.dailyPortfolioChangePct > 0 ? theme?.palette?.success?.dark : theme?.palette?.error?.dark, ml: 0.5 }}
                            >
                              {formatAmount(tableData?.dailyPortfolioChange)}(
                              {tableData?.dailyPortfolioChangePct > 0
                                ? `+${tableData?.dailyPortfolioChangePct}`
                                : `${tableData?.dailyPortfolioChangePct || ''}`}
                              ) %
                            </Typography>
                          </>
                        ) : null}
                      </Stack>
                    </Stack>
                  </Stack>
                </Card>
              </Stack>
            )}
            <Stack sx={{ backgroundColor: theme?.palette?.common?.white, p: 2, borderRadius: 2, minHeight: '40vh' }}>
              {getPortfolioAllocation?.isPending ? (
                <Stack sx={{ minHeight: '40vh', alignItems: 'center', justifyContent: 'center' }}>
                  <Spinner />
                </Stack>
              ) : (
                <>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Portfolio Allocation
                  </Typography>
                  {!isEmpty ? (
                    <PortfolioChart data={portfolioAllocation} />
                  ) : (
                    <Stack alignItems="center" justifyContent="center" minHeight="20vh">
                      <Typography variant="subtitle2">No data available</Typography>
                    </Stack>
                  )}
                </>
              )}
            </Stack>
          </Stack>
        </Grid>
        <Grid
          size={{
            lg: 8,
            md: 12,
            sm: 12
          }}>
          <Stack gap={2}>
            <Stack sx={{ backgroundColor: theme?.palette?.common?.white, borderRadius: 2, border: `1px solid ${theme?.palette?.grey[300]}` }}>
              <PerformanceGraphs clientID={clientID} isClient={isClient} />
            </Stack>
            <Stack
              sx={{ backgroundColor: theme?.palette?.common?.white, borderRadius: 2, border: `1px solid ${theme?.palette?.grey[300]}`, overflow: 'hidden' }}
            >
              {shouldShowTabs && (
                <Tabs value={currentTab} onChange={handleChangeTab} sx={{ pl: 2 }}>
                  {tabs?.map((tab) => (
                    <Tab
                      key={tab.value}
                      iconPosition="end"
                      value={tab.value}
                      label={tab.label}
                      icon={
                        <Label variant={'soft'} color={'default'}>
                          {tab.count}
                        </Label>
                      }
                    />
                  ))}
                </Tabs>
              )}

              <AccountTable data={tableData} isLoading={portfolioAccounts?.isPending} table={table} currentTab={currentTab} isClient={isClient} />
            </Stack>
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
};
